<template>
  <div :class="['navbar', boxShadow]">
    <Logo class="navbar__logo"/>
    <div class="navbar__wrapper">
      <ul class="el-menu el-menu--horizontal">
        <li class="el-menu-item is-first">
          <el-button circle
                     class="button-menu el-button--default"
                     @click="handleClickMenu">
            <font-awesome-icon :icon="['fas', 'bars']"/>
          </el-button>
        </li>
        <li>
          <div class="el-menu-item__legend">
            {{ $t('layout.navbar.title') }}
          </div>
        </li>
        <li class="el-menu-item el-menu-item__right">
          <div class="el-menu-item__right__phone">
            <span class="el-menu-item__right__phone__text">
              {{ this.$t('base.phoneNumber') }}
            </span>
          </div>
          <el-select
            v-if="projects.length > 1"
            v-model="projectSelected"
            :disabled="projectSelectorDisabled"
            value-key="token"
            class="el-menu-item__right__select-project">
            <el-option
              v-for="project in projects"
              :key="project.token"
              :label="`${project.departure_city} - ${project.arrival_city} (${project.num_client})`"
              :value="project">
            </el-option>
          </el-select>
          <el-tooltip
            v-else-if="projects.length === 1 && projectSelected?.num_client"
            :content="$t('layout.navbar.fileNumber')"
            placement="bottom">
            <span class="el-menu-item__right__select-project el-menu-item__right__num-client">
              {{ projectSelected.num_client }}
            </span>
          </el-tooltip>
          <el-dropdown
            class="el-menu-item__right__dropdown"
            @command="dropdownClick"
            menu-trigger="click">
            <span class="el-menu-item__right__dropdown__user">
              <font-awesome-icon
                :icon="['fal', 'user-circle']"
                class="el-menu-item__right__user-button" />
              <span class="el-menu-item__right__user-text">{{ name }}</span>
              <el-icon class="el-icon-arrow-down el-icon--right">
                <ArrowDown />
              </el-icon>
            </span>
            <template #dropdown>
            <el-dropdown-menu class="el-menu-item__right__dropdown">
              <el-dropdown-item v-if="!profileDisabled" command="profile">
                <font-awesome-icon
                  :icon="['fal', 'user']"
                  style="margin-right:10px"/>
                  <span>{{ $t('layout.navbar.account') }}</span>
              </el-dropdown-item>
              <el-dropdown-item command="signOut">
                <font-awesome-icon
                  :icon="['fal', 'sign-out-alt']"
                  style="margin-right:10px"/>
                  <span>{{ $t('layout.navbar.signOut') }}</span>
              </el-dropdown-item>
            </el-dropdown-menu>
            </template>
          </el-dropdown>
        </li>
        <li>
          <div v-if="isActived"
              class="el-menu__bg"
              @click="isActived = !isActived"></div>
          <burger-menu :is-actived="isActived"/>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from 'pinia';
import { useAuthStore, useDemandStore, useProjectStore } from '@/stores';
import cookie from '@nextories/vue-core-components/src/utils/helpers/cookie';
import BurgerMenu from '@/components/layout/BurgerMenu.vue';
import Logo from '@/components/layout/Logo.vue';
import { BREAKPOINTS } from '@/types/breakpoints';
import WrapperNavbar from '../../utils/components/WrapperNavbar.vue';
import { ArrowDown } from '@element-plus/icons-vue';

export default {
  name: 'navbar',
  extends: WrapperNavbar,
  components: {
    BurgerMenu,
    Logo,
    ArrowDown,
  },
  data() {
    return {
      project: '',
      isActived: false,
    };
  },
  computed: {
    ...mapState(useDemandStore, ['demand']),
    ...mapState(useProjectStore, {
      projectSelectedStore: 'projectSelected',
      projects: 'projects',
    }),
    projectSelected: {
      get() {
        return this.projectSelectedStore;
      },
      set(projectSelected) {
        this.setProjectSelected(projectSelected);
      },
    },
    name() {
      if (!this.demand || !this.demand.first_name) {
        return '';
      }
      if (this.demand.first_name.length < 12) {
        return this.demand.first_name;
      }
      return this.$t('layout.navbar.profile');
    },
    profileDisabled() {
      return this.projectSelected.params.disable_password;
    },
    projectSelectorDisabled() {
      const disabledRoutes = [
        'choice',
        'services',
      ];
      return disabledRoutes.includes(this.$route.name);
    },
  },
  methods: {
    ...mapActions(useAuthStore, ['logout']),
    ...mapActions(useProjectStore, ['setProjectSelected']),
    dropdownClick(commands) {
      this[commands]();
    },
    signOut() {
      cookie.deleteCookie(import.meta.env.VITE_APP_COOKIE_TOKEN_NAME);
      this.logout();
      this.$router.push({ name: 'login' });
    },
    profile() {
      this.$router.push({ name: 'profile' }).catch((err) => err);
    },
    resize() {
      if (this.isActived && BREAKPOINTS.desktop < window.innerWidth) {
        this.isActived = !this.isActived;
      }
    },
    handleClickMenu() {
      this.isActived = !this.isActived;
    },
  },
  watch: {
    '$route.name': {
      handler() {
        this.isActived = false;
      },
    },
    projectSelected() {
      this.isActived = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.navbar {
  border-bottom: solid 1px var(--nxt-color-light-grey)!important;
  position: fixed;
  display: flex;
  z-index: 50;
  top: 0;
  left: 0;
  width: 100%;
  height: 60px;
  color: var(--nxt-color-white);

  &__logo {
    display: none;

    @include breakpoint(desktop) {
      display: block;
    }
  }

  &__wrapper {
    flex: 1 1 0%;
  }

  .el-menu {
    height: 60px;
    border: none!important;
    justify-content: space-between;
    background-color: transparent;

    &-item__legend,
    &-item__right__phone,
    &-item__right__select-project,
    :deep(.el-sub-menu.el-sub-menu__hide-arrow) {
      display: none;
    }

    &-item.is-first,
    .button-menu {
      @include breakpoint(desktop) {
        display: none;
      }
    }

    &-item__legend,
    &-item__right__phone,
    &-item__right__select-project {
      @include breakpoint(wide) {
        display: inline-block;
      }
    }

    .button-menu {
      padding: 12px;
      width: initial;
      height: initial;

      :deep(span) {
        display: flex;
      }
    }

    &-item {
      cursor: default;

      &.is-first {
        padding-left: 10px;
      }

      &:hover, &:focus, &:active {
        background-color: transparent;
      }

      &__legend {
        margin-top: 18px;
        color: var(--nxt-color-white);
        margin-left: 20px;
      }

      &__right {
        margin-bottom: 0;
        outline: none;
        justify-content: flex-end;
        flex: 1;

        &__phone {
          margin-right: 20px;

          &__text {
            border-radius: 3px;
            padding: 5px 10px;
            border: var(--nxt-color-white) 1px solid;
            font-size: 18px;
            color: var(--nxt-color-white);
          }
        }

        &__dropdown {
          cursor: pointer;
          color: var(--nxt-color-white);
          vertical-align: middle;

          &__user {
            display: flex;
            align-items: center;

            &:focus-visible {
              outline: none;
            }

            .el-icon-arrow-down {
              font-size: 14px;
              width: auto;
              margin-right: 0;
            }
          }
        }

        &__select-project {
          margin-right: 20px;
          width: 220px;
        }

        &__num-client {
          width: auto;
          line-height: 35px;
          font-size: 16px;
          color: var(--nxt-color-white);
          cursor: default;
          vertical-align: middle;
        }

        &__user-button {
          outline: none;
          vertical-align: top;
          margin-right: 10px;
          width: 2.5em;
          height: 2.5em;
        }

        &__user-text {
          outline: none;
          user-select: none;
          line-height: 35px;
          height: 35px;
          font-size: 20px;
        }
      }
    }

    &__bg {
      background-color: var(--nxt-color-black);
      position: absolute;
      width: 100%;
      height: 100%;
      top: 0;
      left: 0;
      opacity: 0.6;
      z-index: 1000;
      position: fixed;
    }
  }

  &--box-shadow {
    color: var(--nxt-color-primary);
    background-color: var(--nxt-color-light-grey);
    box-shadow: 0 2px 12px 0 rgba(0,0,0,.1);

    .el-menu-item__legend {
      color: var(--nxt-color-primary);
    }

    .el-menu-item__right__phone__text {
      border: var(--nxt-color-primary) 1px solid;
      color: var(--nxt-color-primary);
    }

    .el-menu-item__right__dropdown {
      color: var(--nxt-color-primary);
    }

    .el-menu-item__right__num-client {
      color: var(--nxt-color-primary);
    }
  }
}

</style>
